import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import Sidebar from './components/Sidebar';
import Canvas from './components/Canvas';
import LayersPanel from './components/LayersPanel';
import About from './components/About';
import Contact from './components/Contact';
import MobileLanding from './components/MobileLanding';
import { uoftLogoDataUri } from './assets/uoftLogoBase64';
import './App.css';

const socket = io({ path: '/socket.io' });

// Composite a new RGBA image on top of a base image using an offscreen canvas.
// Returns a base64 PNG string. If no base, returns the new image as-is.
function compositeImages(baseB64, topB64) {
  return new Promise((resolve) => {
    if (!baseB64) { resolve(topB64); return; }
    const canvas = document.createElement('canvas');
    const baseImg = new Image();
    const topImg  = new Image();
    let loaded = 0;
    const onLoad = () => {
      if (++loaded < 2) return;
      canvas.width  = baseImg.naturalWidth;
      canvas.height = baseImg.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(baseImg, 0, 0);
      ctx.drawImage(topImg,  0, 0);
      resolve(canvas.toDataURL('image/png').split(',')[1]);
    };
    baseImg.onload = onLoad;
    topImg.onload  = onLoad;
    baseImg.src = `data:image/png;base64,${baseB64}`;
    topImg.src  = `data:image/png;base64,${topB64}`;
  });
}

const DEFAULT_PARAMS = {
  prompt: '',
  prompt_a: '',
  prompt_b: '',
  prompts: [
    { text: '', color: '#e8455c', weight: 1 },
  ],
  directional_prompts: [
    { from: '', to: '', scale: 1 },
  ],
  from_concept: '',
  to_concept: '',
  alpha: 0.5,
  scale: 1.0,
  steps: 40,
  guide_scale: 7,
  single_stroke: 20,
  overcoat: 100,
  width: 1344,
  height: 768,
};

export default function App() {
  const [stencilActive, setStencilActive] = useState(false);
  const [params,      setParams]      = useState(DEFAULT_PARAMS);
  const [generating,  setGenerating]  = useState(false);
  const [step,        setStep]        = useState(0);
  const [totalSteps,  setTotalSteps]  = useState(0);
  const [layers,      setLayers]      = useState([
    { id: 'layer-1', name: 'Layer 1', imageB64: null, visible: true, opacity: 1,
      stepPreviews: {}, generationChain: [], totalSteps: 0 },
    { id: 'layer-2', name: 'Layer 2', imageB64: null, visible: true, opacity: 1,
      stepPreviews: {}, generationChain: [], totalSteps: 0 },
    { id: 'layer-3', name: 'Layer 3', imageB64: null, visible: true, opacity: 1,
      stepPreviews: {}, generationChain: [], totalSteps: 0 },
  ]);
  const [activeLayerId, setActiveLayerId] = useState('layer-1');
  const [strokes,     setStrokes]     = useState([]);
  const [brushRadius, setBrushRadius] = useState(50);
  const [, setStatusMsg] = useState('');
  const [menuOpen,    setMenuOpen]    = useState(false);
  const [queuePos,    setQueuePos]    = useState(null);
  const [paletteWeights, setPaletteWeights] = useState([]);
  const [theme, setTheme] = useState(() => localStorage.getItem('pp-theme') || 'dark');
  const [generatingStencil, setGeneratingStencil] = useState(false);
  const [modelOnline,    setModelOnline]    = useState(true);
  const [modelStarted,   setModelStarted]   = useState(false);
  const [modelDismissed, setModelDismissed] = useState(false);
  const [startError,     setStartError]     = useState(null);
  const pollIntervalRef = useRef(null);

  // Scrubber state (UI — reset when switching layers)
  const [resumeStep,        setResumeStep]        = useState(null);
  const [scrubImage,        setScrubImage]        = useState(null);
  const [paletteCursorPos,  setPaletteCursorPos]  = useState(null);
  const menuRef          = useRef(null);
  const totalStepsRef    = useRef(0);
  const compositeB64Ref  = useRef(null);
  const layersRef        = useRef(layers);
  const isStencilGenRef      = useRef(false);
  const preStencilImageRef   = useRef(null);
  const generatingLayerIdRef = useRef(null);
  const activeLayerIdRef     = useRef(activeLayerId);
  useEffect(() => { activeLayerIdRef.current = activeLayerId; }, [activeLayerId]);
  useEffect(() => { layersRef.current = layers; }, [layers]);

  // When switching layers: reset scrub UI + restore that layer's totalSteps for ScrubBar display
  useEffect(() => {
    setResumeStep(null);
    setScrubImage(null);
    // Don't clobber totalSteps mid-generation — the progress counter belongs to the generating layer
    if (!generating) {
      const layer = layersRef.current.find(l => l.id === activeLayerId);
      setTotalSteps(layer?.totalSteps ?? 0);
    }
  }, [activeLayerId]); // eslint-disable-line react-hooks/exhaustive-deps
  const navigate = useNavigate();
  const location = useLocation();

  const showAbout   = location.pathname === '/about';
  const showContact = location.pathname === '/contacts';

  // Re-show the offline modal when user navigates back to main
  useEffect(() => {
    if (location.pathname === '/') setModelDismissed(false);
  }, [location.pathname]);

  const switchTheme = (t) => { setTheme(t); localStorage.setItem('pp-theme', t); setMenuOpen(false); };

  // Close menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);


  // ---------------------------------------------------------------------------
  // Socket event listeners
  // ---------------------------------------------------------------------------
  useEffect(() => {

    socket.on('queued', (data) => {
      setQueuePos(data.position);
    });

    socket.on('started', () => {
      setQueuePos(null);
    });

    socket.on('progress', (data) => {
      setStep(data.step);
      setTotalSteps(data.total);
      totalStepsRef.current = data.total;
      if (data.preview) {
        const genLayerId = generatingLayerIdRef.current;
        const step = data.step;
        const applyPreview = (img) => {
          setLayers(prev => prev.map(l => l.id === genLayerId ? {
            ...l,
            imageB64: img,
            stepPreviews: { ...l.stepPreviews, [step]: img },
          } : l));
        };
        if (isStencilGenRef.current) {
          compositeImages(preStencilImageRef.current, data.preview).then(applyPreview);
        } else {
          applyPreview(data.preview);
        }
      }
    });

    socket.on('result', (data) => {
      const genLayerId = generatingLayerIdRef.current;
      const total = totalStepsRef.current;
      const finalize = (image) => {
        setLayers(prev => prev.map(l => {
          if (l.id !== genLayerId) return l;
          return {
            ...l,
            imageB64: image,
            stepPreviews: { ...l.stepPreviews, [total]: image },
            generationChain: l.generationChain.map((n, i) =>
              i === l.generationChain.length - 1 ? { ...n, toStep: total } : n
            ),
            totalSteps: total,
          };
        }));
        setTotalSteps(total);
        setGenerating(false);
        setGeneratingStencil(false);
        setQueuePos(null);
        setStep(0);
        generatingLayerIdRef.current = null;
      };
      if (isStencilGenRef.current) {
        compositeImages(preStencilImageRef.current, data.image).then(finalize);
      } else {
        finalize(data.image);
      }
    });


    socket.on('error', (data) => {
      setGenerating(false);
      setGeneratingStencil(false);
      setQueuePos(null);
      generatingLayerIdRef.current = null;
      console.error('Generation error:', data?.message ?? data);
    });

    socket.on('status', (data) => {
      setStatusMsg(data.message);
    });

    return () => {
      socket.off('queued');
      socket.off('started');
      socket.off('connect');
      socket.off('disconnect');
      socket.off('progress');
      socket.off('result');
      socket.off('error');
      socket.off('status');
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Model health polling
  // ---------------------------------------------------------------------------
  const checkModelHealth = useCallback(() => {
    fetch('/api/health')
      .then(r => r.json())
      .then(data => {
        const online = data.model !== 'unreachable';
        setModelOnline(online);
        if (online) { setModelStarted(false); }
      })
      .catch(() => setModelOnline(false));
  }, []);

  useEffect(() => {
    checkModelHealth();
    pollIntervalRef.current = setInterval(checkModelHealth, 10000);
    return () => clearInterval(pollIntervalRef.current);
  }, [checkModelHealth]);

  // Poll faster while waiting for model to start
  useEffect(() => {
    if (modelStarted && !modelOnline) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = setInterval(checkModelHealth, 5000);
    } else {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = setInterval(checkModelHealth, 10000);
    }
  }, [modelStarted, modelOnline, checkModelHealth]);

  const handleRequestModelStart = useCallback(() => {
    setStartError(null);
    fetch('/api/model/start', { method: 'POST' })
      .then(r => r.json())
      .then(data => {
        if (data.status === 'starting' || data.status === 'already_running') {
          setModelStarted(true);
        } else if (data.status === 'rate_limited') {
          const mins = Math.ceil(data.retry_after / 60);
          setStartError(`Too many requests. Try again in ${mins} min.`);
        } else {
          setStartError(data.message || 'Could not start model.');
        }
      })
      .catch(() => setStartError('Request failed.'));
  }, []);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------
  const handleGenerate = useCallback(() => {
    if (generating) return;

    const prompts = params.prompts || [];
    const hasStrokes = strokes.length > 0;
    const effectiveMode = hasStrokes ? 'stencil' : (prompts.length > 1 ? 'mixing' : 'standard');

    // Require a palette selection for non-stencil mixing
    const hasWeights = paletteWeights.length === prompts.length && paletteWeights.some(w => w > 0.001);
    if (!hasWeights) {
      console.warn('[PromptPaint] No palette selection — place the cursor on a prompt dot or mix before generating.');
      return;
    }

    const weightedPrompts = prompts.map((p, i) => ({
      ...p,
      weight: hasWeights ? paletteWeights[i] : 1,
    }));

    const activeId = activeLayerIdRef.current;
    const activeLayer = layersRef.current.find(l => l.id === activeId);
    const activeLayerImage = activeLayer?.imageB64 || '';

    const payload = {
      ...params,
      prompts: weightedPrompts,
      mode: effectiveMode,
      prompt: prompts[0]?.text || '',
      strokes: hasStrokes ? strokes : undefined,
      image_b64: hasStrokes ? activeLayerImage : undefined,
      resume_step: resumeStep ?? -1,
    };

    // Update this layer's stepPreviews and generationChain
    setLayers(prev => prev.map(l => {
      if (l.id !== activeId) return l;
      const trimmedPreviews = resumeStep === null
        ? {}
        : Object.fromEntries(Object.entries(l.stepPreviews).filter(([k]) => Number(k) <= resumeStep));
      let newChain = l.generationChain;
      if (paletteCursorPos) {
        const fromStep = resumeStep ?? 0;
        newChain = [
          ...l.generationChain
            .filter(n => n.fromStep < fromStep)
            .map(n => (n.toStep === null || n.toStep > fromStep) ? { ...n, toStep: fromStep } : n),
          { pos: paletteCursorPos, fromStep, toStep: null },
        ];
      }
      return { ...l, stepPreviews: trimmedPreviews, generationChain: newChain };
    }));

    isStencilGenRef.current      = hasStrokes;
    preStencilImageRef.current   = hasStrokes ? activeLayerImage : null;
    generatingLayerIdRef.current = activeLayerIdRef.current;
    setGeneratingStencil(hasStrokes);
    setScrubImage(null);
    setResumeStep(null);
    setStencilActive(false);
    setGenerating(true);
    setStep(resumeStep ?? 0);
    setTotalSteps(params.steps || 40);
    totalStepsRef.current = params.steps || 40;

    if (resumeStep === null && !hasStrokes) {
      setLayers(prev => prev.map(l => l.id === activeId ? { ...l, imageB64: null } : l));
    } else if (resumeStep !== null) {
      const resumeImg = activeLayer?.stepPreviews?.[resumeStep] ?? null;
      setLayers(prev => prev.map(l => l.id === activeId ? { ...l, imageB64: resumeImg } : l));
    }
    // stencil fresh generation: leave active layer imageB64 unchanged
    setStatusMsg('Starting…');
    socket.emit('generate', payload);
  }, [generating, params, strokes, paletteWeights, resumeStep, paletteCursorPos]);

  // ---------------------------------------------------------------------------
  // Layer management
  // ---------------------------------------------------------------------------
  const addLayer = useCallback((name) => {
    const id = `layer-${Date.now()}`;
    setLayers(prev => [...prev, {
      id, name: name || `Layer ${prev.length + 1}`,
      imageB64: null, visible: true, opacity: 1,
      stepPreviews: {}, generationChain: [], totalSteps: 0,
    }]);
    setActiveLayerId(id);
  }, []);

  const removeLayer = useCallback((id) => {
    setLayers(prev => {
      if (prev.length <= 1) return prev;
      const next = prev.filter(l => l.id !== id);
      setActiveLayerId(cur => {
        if (cur !== id) return cur;
        const idx = prev.findIndex(l => l.id === id);
        return next[Math.min(idx, next.length - 1)].id;
      });
      return next;
    });
  }, []);

  const toggleLayerVisible = useCallback((id) => {
    setLayers(prev => prev.map(l => l.id === id ? { ...l, visible: !l.visible } : l));
  }, []);

  const handleLayerUpdate = useCallback((id, b64) => {
    setLayers(prev => prev.map(l => l.id === id ? { ...l, imageB64: b64 } : l));
  }, []);

  const reorderLayers = useCallback((fromIndex, toIndex) => {
    setLayers(prev => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  const handleScrub = useCallback((s) => {
    const activeLayer = layersRef.current.find(l => l.id === activeLayerIdRef.current);
    const layerTotal = activeLayer?.totalSteps ?? 0;
    if (s >= layerTotal) { setResumeStep(null); setScrubImage(null); return; }
    setResumeStep(s);
    setScrubImage(activeLayer?.stepPreviews?.[s] ?? null);
  }, []);

  const handleUnscrub = useCallback(() => {
    setResumeStep(null);
    setScrubImage(null);
  }, []);


  // Keyboard shortcut: Cmd/Ctrl+Enter to generate
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') handleGenerate();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleGenerate]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
    <MobileLanding />
    <div className={`app ${theme}`}>
      <header className="app-header">
        <div className="header-title">
          <div className="logo-container logo-clickable" onClick={() => navigate('/')}>
            <h1 className="logo">PromptPaint</h1>
            <svg className="logo-brush-deco" viewBox="0 0 360 66" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <defs>
                <linearGradient id="hdr-grad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%"   stopColor="#e91e8c"/>
                  <stop offset="20%"  stopColor="#ff5722"/>
                  <stop offset="42%"  stopColor="#ffc107"/>
                  <stop offset="62%"  stopColor="#4caf50"/>
                  <stop offset="82%"  stopColor="#00bcd4"/>
                  <stop offset="100%" stopColor="#2196f3"/>
                </linearGradient>
              </defs>
              {/* shadow undercoat */}
              <path d="M 4,44 Q 90,30 180,36 Q 270,42 318,28" stroke="rgba(0,0,0,0.3)" strokeWidth="38" strokeLinecap="round" fill="none"/>
              {/* rainbow stroke */}
              <path d="M 4,42 Q 90,28 180,34 Q 270,40 318,26" stroke="url(#hdr-grad)" strokeWidth="34" strokeLinecap="round" fill="none" opacity="0.75"/>
              {/* shimmer */}
              <path d="M 8,32 Q 90,19 180,25 Q 270,31 314,17" stroke="rgba(255,255,255,0.2)" strokeWidth="9" strokeLinecap="round" fill="none"/>
              {/* paintbrush at right end, handle upper-right bristles lower-left */}
              <g transform="translate(318,26) rotate(38)">
                <rect x="-5" y="-44" width="10" height="34" rx="3" fill="#7a4520"/>
                <rect x="-1.5" y="-42" width="2" height="30" rx="1" fill="rgba(255,255,255,0.15)"/>
                <ellipse cx="0" cy="-44" rx="5" ry="4" fill="#1e0a02"/>
                <rect x="-6" y="-12" width="12" height="11" rx="1" fill="#aaaaaa"/>
                <line x1="-6" y1="-8" x2="6" y2="-8" stroke="rgba(255,255,255,0.4)" strokeWidth="0.8"/>
                <path d="M -6,0 C -7,8 -16,18 -17,30 L 17,30 C 16,18 7,8 6,0 Z" fill="#c4924a"/>
                <line x1="-5" y1="0" x2="-15" y2="30" stroke="#8b6020" strokeWidth="0.8" opacity="0.5"/>
                <line x1="-2" y1="0" x2="-8"  y2="30" stroke="#8b6020" strokeWidth="0.8" opacity="0.5"/>
                <line x1="0"  y1="0" x2="0"   y2="30" stroke="#8b6020" strokeWidth="0.8" opacity="0.5"/>
                <line x1="2"  y1="0" x2="8"   y2="30" stroke="#8b6020" strokeWidth="0.8" opacity="0.5"/>
                <line x1="5"  y1="0" x2="15"  y2="30" stroke="#8b6020" strokeWidth="0.8" opacity="0.5"/>
                <path d="M -17,25 Q 0,29 17,25 L 17,30 Q 0,34 -17,30 Z" fill="#2196f3" opacity="0.9"/>
                <path d="M -17,25 Q -12,27 -8,26 L -8,30 Q -12,31 -17,30 Z" fill="#4caf50" opacity="0.85"/>
                <path d="M 8,26 Q 12,27 17,25 L 17,30 Q 12,31 8,30 Z" fill="#ffc107" opacity="0.85"/>
              </g>
            </svg>
          </div>
          <p className="logo-subtitle">Prompt Like a Painter</p>
        </div>
        <div className="header-right">
          <img src={uoftLogoDataUri} alt="University of Toronto" className="uoft-logo" />
        </div>
      </header>

      {!modelOnline && !modelDismissed && (
        <div className="model-offline-overlay">
          <div className="model-offline-modal">
            {/* Logo — same layering as header */}
            <div className="model-offline-logo-wrap">
              <div className="logo-container">
                <h2 className="logo model-offline-logo-text">PromptPaint</h2>
                <svg className="logo-brush-deco" viewBox="0 0 360 66" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <defs>
                    <linearGradient id="mo-grad" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%"   stopColor="#e91e8c"/>
                      <stop offset="20%"  stopColor="#ff5722"/>
                      <stop offset="42%"  stopColor="#ffc107"/>
                      <stop offset="62%"  stopColor="#4caf50"/>
                      <stop offset="82%"  stopColor="#00bcd4"/>
                      <stop offset="100%" stopColor="#2196f3"/>
                    </linearGradient>
                  </defs>
                  <path d="M 4,44 Q 90,30 180,36 Q 270,42 318,28" stroke="rgba(0,0,0,0.3)" strokeWidth="38" strokeLinecap="round" fill="none"/>
                  <path d="M 4,42 Q 90,28 180,34 Q 270,40 318,26" stroke="url(#mo-grad)" strokeWidth="34" strokeLinecap="round" fill="none" opacity="0.75"/>
                  <path d="M 8,32 Q 90,19 180,25 Q 270,31 314,17" stroke="rgba(255,255,255,0.2)" strokeWidth="9" strokeLinecap="round" fill="none"/>
                  <g transform="translate(318,26) rotate(38)">
                    <rect x="-5" y="-44" width="10" height="34" rx="3" fill="#7a4520"/>
                    <rect x="-1.5" y="-42" width="2" height="30" rx="1" fill="rgba(255,255,255,0.15)"/>
                    <ellipse cx="0" cy="-44" rx="5" ry="4" fill="#1e0a02"/>
                    <rect x="-6" y="-12" width="12" height="11" rx="1" fill="#aaaaaa"/>
                    <line x1="-6" y1="-8" x2="6" y2="-8" stroke="rgba(255,255,255,0.4)" strokeWidth="0.8"/>
                    <path d="M -6,0 C -7,8 -16,18 -17,30 L 17,30 C 16,18 7,8 6,0 Z" fill="#c4924a"/>
                    <line x1="-5" y1="0" x2="-15" y2="30" stroke="#8b6020" strokeWidth="0.8" opacity="0.5"/>
                    <line x1="-2" y1="0" x2="-8"  y2="30" stroke="#8b6020" strokeWidth="0.8" opacity="0.5"/>
                    <line x1="0"  y1="0" x2="0"   y2="30" stroke="#8b6020" strokeWidth="0.8" opacity="0.5"/>
                    <line x1="2"  y1="0" x2="8"   y2="30" stroke="#8b6020" strokeWidth="0.8" opacity="0.5"/>
                    <line x1="5"  y1="0" x2="15"  y2="30" stroke="#8b6020" strokeWidth="0.8" opacity="0.5"/>
                    <path d="M -17,25 Q 0,29 17,25 L 17,30 Q 0,34 -17,30 Z" fill="#2196f3" opacity="0.9"/>
                    <path d="M -17,25 Q -12,27 -8,26 L -8,30 Q -12,31 -17,30 Z" fill="#4caf50" opacity="0.85"/>
                    <path d="M 8,26 Q 12,27 17,25 L 17,30 Q 12,31 8,30 Z" fill="#ffc107" opacity="0.85"/>
                  </g>
                </svg>
              </div>
              <p className="logo-subtitle">Prompt Like a Painter</p>
            </div>

            <div className="model-offline-divider" />

            {/* Centered title, no dot */}
            <h3 className="model-offline-title">
              {modelStarted ? 'Starting Up' : 'Model Offline'}
            </h3>

            {!modelStarted ? (
              <>
                <p className="model-offline-body">
                  PromptPaint runs on a personal server with a dedicated GPU, not a cloud provider.
                  Occasionally the GPU is needed for other projects, so the model gets taken offline temporarily.
                </p>
                <p className="model-offline-body">
                  You can request it to start back up and it should be ready within a minute or two.
                  If it doesn't respond, feel free to reach out and I'll get it going.
                </p>
                {startError && <p className="model-offline-error">{startError}</p>}
                <div className="model-offline-actions">
                  <button className="model-offline-btn-primary" onClick={handleRequestModelStart}>
                    Request Start
                  </button>
                  <button className="model-offline-btn-secondary" onClick={() => { setModelDismissed(true); navigate('/contacts'); }}>
                    Contact Me
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="model-offline-body">
                  The model is loading onto the GPU. This usually takes about a minute.
                  The page will update automatically once it's ready.
                </p>
                <div className="model-offline-starting">
                  <span className="model-offline-spinner" />
                  <span>Loading model…</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <main className="app-body">
        {showAbout   && <About   onClose={() => navigate('/')} />}
        {showContact && <Contact onClose={() => navigate('/')} />}
        <Sidebar
          params={params}           onParamsChange={setParams}
          onGenerate={handleGenerate}
          generating={generating}
          queuePos={queuePos}
          step={step}               totalSteps={totalSteps}
          brushRadius={brushRadius} onBrushRadiusChange={setBrushRadius}
          stencilActive={stencilActive} onStencilActiveChange={setStencilActive}
          hasStrokes={strokes.length > 0}
          onPaletteWeightsChange={setPaletteWeights}
          onPaletteCursorPosChange={setPaletteCursorPos}
          generationChain={layers.find(l => l.id === activeLayerId)?.generationChain ?? []}
          stepPreviews={layers.find(l => l.id === activeLayerId)?.stepPreviews ?? {}}
          resumeStep={resumeStep}
          onScrub={handleScrub}
          onUnscrub={handleUnscrub}
          generatingStencil={generatingStencil}
        />

        <section className="canvas-area">

          {/* Layers column — top-right of canvas area */}
          <LayersPanel
            layers={layers}
            activeLayerId={activeLayerId}
            onSetActive={setActiveLayerId}
            onAdd={addLayer}
            onRemove={removeLayer}
            onToggleVisible={toggleLayerVisible}
            onReorder={reorderLayers}
          />

          {/* Hamburger menu — bottom-left of canvas area */}
          <div className="canvas-menu-container" ref={menuRef}>
            <button className="hamburger-menu" onClick={() => setMenuOpen(o => !o)}>
              <span></span>
              <span></span>
              <span></span>
            </button>
            {menuOpen && (
              <div className="canvas-dropdown">
                <div className="canvas-menu-label">Theme</div>
                <button className={`canvas-menu-item ${theme === 'light' ? 'active' : ''}`} onClick={() => switchTheme('light')}>
                  <svg className="canvas-menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                  </svg>
                  Light
                </button>
                <button className={`canvas-menu-item ${theme === 'dark' ? 'active' : ''}`} onClick={() => switchTheme('dark')}>
                  <svg className="canvas-menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                  </svg>
                  Dark
                </button>
                <div className="canvas-menu-label">More</div>
                <button className="canvas-menu-item" onClick={() => { navigate('/about'); setMenuOpen(false); }}>
                  <svg className="canvas-menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                  About
                </button>
                <button className="canvas-menu-item" onClick={() => { navigate('/contacts'); setMenuOpen(false); }}>
                  <svg className="canvas-menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                  </svg>
                  Contact
                </button>
              </div>
            )}
          </div>

          <Canvas
            layers={layers}
            activeLayerId={activeLayerId}
            activeLayerOverride={scrubImage}
            onLayerUpdate={handleLayerUpdate}
            onCompositeChange={(b64) => { compositeB64Ref.current = b64; }}
            isPreview={generating || !!scrubImage}
            stencilMode={stencilActive}
            onStencilActiveChange={setStencilActive}
            generating={generating}
            brushRadius={brushRadius}
            onStrokesChange={setStrokes}
            hasStrokes={strokes.length > 0}
            width={params.width}
            height={params.height}
            onSizeChange={({ width, height }) => setParams(p => ({ ...p, width, height }))}
          />
          <p className="canvas-area-footer">
            Based on <em>PromptPaint</em> by John Joon Young Chung &amp; Eytan Adar, University of Michigan · UIST 2023 · Implemented by Thomas Nguyen, University of Toronto
          </p>
        </section>
      </main>
    </div>
    </>
  );
}
